import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getLatestMembers } from "../services/dashboardServices";
import { useMessage } from "./MessageContext";
import { useNotification } from "./NotificationContext";
import { useContactForm } from "./ContactFormContext";

const DEBUG_HOME_SHORTCUT_COUNTERS = false;

function debugLog(...args) {
  if (DEBUG_HOME_SHORTCUT_COUNTERS) {
    console.log(...args);
  }
}

function debugWarn(...args) {
  if (DEBUG_HOME_SHORTCUT_COUNTERS) {
    console.warn(...args);
  }
}

function debugError(...args) {
  if (DEBUG_HOME_SHORTCUT_COUNTERS) {
    console.error(...args);
  }
}

function coerceCount(value) {
  const count = Number(value);

  if (!Number.isFinite(count) || count <= 0) return 0;

  return Math.floor(count);
}

function extractArray(data) {
  debugLog("[HomeShortcutCounters] extractArray() - data reçue :", data);

  if (Array.isArray(data)) {
    return data;
  }

  if (!data || typeof data !== "object") {
    return [];
  }

  const directKeys = [
    "rows",
    "items",
    "members",
    "adherents",
    "data",
    "results",
    "list",
  ];

  for (const key of directKeys) {
    if (Array.isArray(data[key])) {
      debugLog(`[HomeShortcutCounters] tableau trouvé dans data.${key}`);
      return data[key];
    }
  }

  if (data.data && typeof data.data === "object") {
    for (const key of directKeys) {
      if (Array.isArray(data.data[key])) {
        debugLog(`[HomeShortcutCounters] tableau trouvé dans data.data.${key}`);
        return data.data[key];
      }
    }
  }

  debugWarn("[HomeShortcutCounters] aucun tableau détecté, retour []");

  return [];
}

function getMemberCreatedAt(member) {
  return (
    member?.createdAt ||
    member?.created_at ||
    member?.date_creation ||
    member?.created_date ||
    member?.inscription_date ||
    member?.date_inscription ||
    null
  );
}

function toLocalDateKey(value) {
  if (!value) return "";

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function countMembersCreatedToday(members) {
  const todayKey = toLocalDateKey(new Date());

  return members.filter((member) => {
    const createdAt = getMemberCreatedAt(member);
    const createdKey = toLocalDateKey(createdAt);

    return createdKey === todayKey;
  }).length;
}

function normalizeMembers(members) {
  return members
    .filter(Boolean)
    .map((member) => ({
      ...member,
      _homeCounterCreatedAt: getMemberCreatedAt(member),
      Groups: Array.isArray(member?.Groups)
        ? member.Groups.map((group) => ({
            id: group?.id,
            nom: group?.nom,
          })).filter((group) => group.nom)
        : [],
    }));
}

function buildShortcutCounterPayload({
  latestMembers,
  unreadMessageCount,
  unreadNotificationCount,
  unreadContactFormCount,
}) {
  const newMembersToday = countMembersCreatedToday(latestMembers);

  const messagesUnread = coerceCount(unreadMessageCount);
  const notificationsUnread = coerceCount(unreadNotificationCount);
  const contactFormsUnread = coerceCount(unreadContactFormCount);

  return {
    counters: {
      // Adhérents
      newMembersToday,
      latestMembersToday: newMembersToday,
      latestMembersTotal: latestMembers.length,

      // Messages privés
      messages: messagesUnread,
      unreadMessages: messagesUnread,
      unreadMessageCount: messagesUnread,

      // Notifications
      notifications: notificationsUnread,
      unreadNotifications: notificationsUnread,
      unreadCount: notificationsUnread,

      // Formulaire de contact / admin
      contactForms: contactFormsUnread,
      adminMessages: contactFormsUnread,
      unreadCountContactForm: contactFormsUnread,

      // Total global indicatif
      totalShortcutAlerts:
        newMembersToday +
        messagesUnread +
        notificationsUnread +
        contactFormsUnread,
    },

    countsByLabel: {
      // Raccourci dashboard adhérents
      "Dashboard Adhérents": newMembersToday,

      // Raccourci messages utilisateur
      Messages: messagesUnread,

      // Raccourci notifications.
      // Si tu n'as pas de raccourci "Notifications", le compteur peut apparaître sur Tableau de Bord.
      Notifications: notificationsUnread,
      "Tableau de Bord": notificationsUnread,

      // Raccourci messages formulaire contact côté admin
      "Message Admin": contactFormsUnread,
      "Messages du formulaire": contactFormsUnread,
    },
  };
}

export default function useHomeShortcutCounters({
  enabled = true,
  refreshOnFocus = true,
} = {}) {
  const requestIdRef = useRef(0);

  const {
    unreadMessageCount = 0,
    refresh: refreshMessages,
  } = useMessage();

  const {
    unreadCount = 0,
    refresh: refreshNotifications,
  } = useNotification();

  const {
    unreadCountContactForm = 0,
    refresh: refreshContactForms,
  } = useContactForm();

  const [loading, setLoading] = useState(Boolean(enabled));
  const [errorMsg, setErrorMsg] = useState("");
  const [latestMembers, setLatestMembers] = useState([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const refreshContextCounters = useCallback(async () => {
    const tasks = [];

    if (typeof refreshMessages === "function") {
      tasks.push(refreshMessages());
    }

    if (typeof refreshNotifications === "function") {
      tasks.push(refreshNotifications());
    }

    if (typeof refreshContactForms === "function") {
      tasks.push(refreshContactForms());
    }

    if (tasks.length === 0) return;

    await Promise.allSettled(tasks);
  }, [refreshContactForms, refreshMessages, refreshNotifications]);

  const fetchLatestMembersCounter = useCallback(async () => {
    const response = await getLatestMembers();

    debugLog("[HomeShortcutCounters] réponse brute getLatestMembers :", response);

    const extracted = extractArray(response);
    const normalized = normalizeMembers(extracted);

    debugLog("[HomeShortcutCounters] latestMembers normalisés :", normalized);

    return normalized;
  }, []);

  const fetchCounters = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      setErrorMsg("");
      setLatestMembers([]);
      setLastUpdatedAt(null);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    try {
      setLoading(true);
      setErrorMsg("");

      const [membersResult] = await Promise.allSettled([
        fetchLatestMembersCounter(),
        refreshContextCounters(),
      ]);

      if (requestId !== requestIdRef.current) return;

      if (membersResult.status === "fulfilled") {
        setLatestMembers(membersResult.value);
      } else {
        debugError(
          "[HomeShortcutCounters] erreur getLatestMembers :",
          membersResult.reason
        );

        setLatestMembers([]);
        setErrorMsg(
          "Impossible de charger certains compteurs des raccourcis."
        );
      }

      setLastUpdatedAt(new Date().toISOString());
    } catch (error) {
      if (requestId !== requestIdRef.current) return;

      debugError(
        "[HomeShortcutCounters] erreur lors du chargement des compteurs :",
        error
      );

      setLatestMembers([]);
      setErrorMsg("Impossible de charger les compteurs des raccourcis.");
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [enabled, fetchLatestMembersCounter, refreshContextCounters]);

  useEffect(() => {
    fetchCounters();
  }, [fetchCounters]);

  useEffect(() => {
    if (!enabled || !refreshOnFocus) return undefined;

    const handleFocus = () => {
      fetchCounters();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchCounters();
      }
    };

    const handleManualRefresh = () => {
      fetchCounters();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener(
      "mac:shortcut-counters-refresh",
      handleManualRefresh
    );

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener(
        "mac:shortcut-counters-refresh",
        handleManualRefresh
      );
    };
  }, [enabled, fetchCounters, refreshOnFocus]);

  const payload = useMemo(() => {
  return buildShortcutCounterPayload({
    latestMembers,
    unreadMessageCount,
    unreadNotificationCount: unreadCount,
    unreadContactFormCount: unreadCountContactForm,
  });
}, [
  latestMembers,
  unreadCount,
  unreadCountContactForm,
  unreadMessageCount,
]);

  return {
    loading,
    errorMsg,

    counters: payload.counters,
    countsByLabel: payload.countsByLabel,

    latestMembers,
    lastUpdatedAt,

    refresh: fetchCounters,

    refreshMessages,
    refreshNotifications,
    refreshContactForms,
  };
}